//! Draws a triangle indicator in the top-right corner of every cell that has
//! an open (unresolved) comment thread, matching Sheets/Excel. Comments are
//! sparse, so all indicators for the current sheet are redrawn whenever the
//! sheet's comments change.
//!
//! Validation/code error markers (CellsTextHashValidations / CellsMarkers)
//! also use the top-right corner; a cell with both shows them overlapping.

import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import { TRIANGLE_SCALE } from '@/app/gridGL/cells/CellsMarkers';
import { generatedTextures } from '@/app/gridGL/generateTextures';
import { colors } from '@/app/theme/colors';
import type { Point } from 'pixi.js';
import { Container, Rectangle, Sprite } from 'pixi.js';

// extra clickable area around the triangle, in world pixels
const HIT_PADDING = 4;

interface CommentMarker {
  x: number;
  y: number;
  rect: Rectangle;
}

export class UIComments extends Container {
  dirty = true;

  private markers: CommentMarker[] = [];

  constructor() {
    super();
    events.on('sheetComments', this.setDirtySheet);
    events.on('changeSheet', this.setDirty);
    events.on('sheetOffsetsUpdated', this.setDirtySheet);
    events.on('resizeHeadingColumn', this.setDirtySheet);
    events.on('resizeHeadingRow', this.setDirtySheet);
  }

  destroy() {
    events.off('sheetComments', this.setDirtySheet);
    events.off('changeSheet', this.setDirty);
    events.off('sheetOffsetsUpdated', this.setDirtySheet);
    events.off('resizeHeadingColumn', this.setDirtySheet);
    events.off('resizeHeadingRow', this.setDirtySheet);
    super.destroy();
  }

  private setDirty = () => {
    this.dirty = true;
  };

  private setDirtySheet = (sheetId: string) => {
    if (sheetId === sheets.current) {
      this.dirty = true;
    }
  };

  update = () => {
    if (!this.dirty) return;
    this.dirty = false;
    this.removeChildren();
    this.markers = [];

    const sheet = sheets.sheet;
    const openThreads = sheet.comments.filter((thread) => !thread.resolved);
    if (openThreads.length === 0) return;

    // one indicator per cell, regardless of how many threads it has
    const cells = new Set<string>();
    openThreads.forEach((thread) => {
      const x = Number(thread.pos.x);
      const y = Number(thread.pos.y);
      const key = `${x},${y}`;
      if (cells.has(key)) return;
      cells.add(key);

      const offset = sheet.getCellOffsets(x, y);
      const sprite = this.addChild(new Sprite(generatedTextures.triangle));
      sprite.tint = colors.cellColorComment;
      // flips the triangle so it properly aligns to the top-right corner
      sprite.scale.set(-TRIANGLE_SCALE, TRIANGLE_SCALE);
      sprite.anchor.set(1, 0);
      sprite.rotation = Math.PI / 2;
      sprite.position.set(offset.x + offset.width, offset.y);
      // the click target is the indicator triangle (padded for clickability),
      // not the whole cell: clicks elsewhere in the cell must keep their
      // normal behavior (select, edit) instead of opening the popover
      const hitWidth = Math.min(sprite.width + HIT_PADDING, offset.width);
      const hitHeight = Math.min(sprite.height + HIT_PADDING, offset.height);
      this.markers.push({
        x,
        y,
        rect: new Rectangle(offset.x + offset.width - hitWidth, offset.y, hitWidth, hitHeight),
      });
    });
  };

  /// Returns the cell whose comment indicator (triangle) contains the world point.
  intersectsComment(world: Point): { x: number; y: number } | undefined {
    const marker = this.markers.find((m) => m.rect.contains(world.x, world.y));
    if (marker) {
      return { x: marker.x, y: marker.y };
    }
  }
}
